import { Inject, Injectable, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { Store } from '@ngrx/store'
import { EventSourcePolyfill } from 'event-source-polyfill/src/eventsource.js'

import { BehaviorSubject, filter, map, of, share, switchMap, withLatestFrom } from 'rxjs'

import {
  AgentInstanceStatus,
  ExecutionPhaseStatus,
} from '@flowx/designer/modules/agent-builder/models/agent.entity'

import { AiSelectors } from '../../store/ai.selectors'
import { AiAgent } from '../ai.dictionary'

export interface AiEventData {
  agent: AiAgent
  message: string
  session_id: string
  type: string
  unique_id: string
  status?: string
}

export interface AgentEventData {
  message: string
  phase: {
    type: string
    agent_id?: string
    plan_id?: string
    node_id?: string
    status?: ExecutionPhaseStatus
    logs?: any[]
  }
  plan: {
    type: string
    agent_id?: string
    plan_id?: string
    status?: AgentInstanceStatus
  }
}

@Injectable({
  providedIn: 'root',
})
export class AiEventsApi {
  #store = inject(Store)

  #accessToken = localStorage.getItem('access_token')
  #baseUrl: string
  #aiEvents$ = new BehaviorSubject<AiEventData | AgentEventData>(null)

  readonly aiEvents$ = this.#aiEvents$.asObservable().pipe(
    filter((event): event is AiEventData => !!event),
    share({ resetOnError: false, resetOnComplete: false, resetOnRefCountZero: false })
  )
  readonly agentEvents$ = this.#aiEvents$.asObservable().pipe(
    filter((event): event is AgentEventData => !!event),
    share({ resetOnError: false, resetOnComplete: false, resetOnRefCountZero: false })
  )
  readonly sessionPendingRequestStatus$ = this.#store.select(AiSelectors.pendingAiResponse).pipe(
    switchMap((pendingAiResponse) =>
      pendingAiResponse
        ? this.aiEvents$.pipe(
            withLatestFrom(this.#store.select(AiSelectors.sessionId)),
            filter(([event, sessionId]) => !!event && event.session_id === sessionId),
            map(([event]) => event.message)
          )
        : of(null)
    )
  )
  readonly sessionPendingRequestStatus = toSignal(this.sessionPendingRequestStatus$)

  constructor(@Inject('BASE_URL') baseUrl: string) {
    this.#baseUrl = baseUrl
  }

  initSseConnection(): EventSourcePolyfill {
    const sseConnection = new EventSourcePolyfill(`${this.#baseUrl}/api/events/userBroadcast`, {
      headers: {
        Authorization: `Bearer ${this.#accessToken}`,
      },
      heartbeatTimeout: 9000000,
      withCredentials: true,
    })

    sseConnection.addEventListener('agent_message', (event: MessageEvent) => {
      const data: AiEventData = JSON.parse(event.data)
      this.#aiEvents$.next(data)
    })

    sseConnection.addEventListener('agent_builder', (event: MessageEvent) => {
      const data: AiEventData = JSON.parse(event.data)
      this.#aiEvents$.next(data)
    })

    return sseConnection
  }
}
